import { useLocation } from 'react-router-dom';

/**
 * Custom hook for managing URL and route detection
 * Provides utilities for checking current route, extracting parameters, and detecting specific routes
 */
export const useUrlManager = () => {
  const location = useLocation();

  /**
   * Get path segments as an array, filtering out empty segments
   * @returns {string[]} Array of path segments
   */
  const getPathSegments = () => {
    return location.pathname.split('/').filter((segment) => segment !== '');
  };

  /**
   * Check if current path starts with the given route path
   * @param {string} routePath - The route path to check
   * @returns {boolean} True if current path starts with routePath
   */
  const isInRoute = (routePath) => {
    return location.pathname.startsWith(routePath);
  };

  /**
   * Get route parameter by index
   * @param {number} index - Index of the parameter
   * @returns {string|null} Parameter value or null if not found
   */
  const getRouteParam = (index) => {
    const segments = getPathSegments();
    return segments[index] || null;
  };

  /**
   * Check if user is in chat route
   * @param {string|null} chatId - Optional chat ID to check against
   * @returns {boolean} True if in chat route and optionally matches chatId
   */
  const isInChat = (chatId = null) => {
    const segments = getPathSegments();
    const isChatRoute = segments[0] === 'inbox' && segments[1] === 'chat';

    if (chatId) {
      return isChatRoute && segments[2] === chatId;
    }

    return isChatRoute;
  };

  /**
   * Get current chat ID from URL
   * @returns {string|null} Current chat ID or null if not in chat route
   */
  const getCurrentChatId = () => {
    if (isInChat()) {
      return getRouteParam(2);
    }
    return null;
  };

  /**
   * Check if user is in a specific section/route
   * @param {string} section - Section name to check
   * @returns {boolean} True if in the specified section
   */
  const isInSection = (section) => {
    const segments = getPathSegments();
    return segments[0] === section;
  };

  /**
   * Get query parameters as an object
   * @returns {Object} Query parameters object
   */
  const getQueryParams = () => {
    const searchParams = new URLSearchParams(location.search);
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    return params;
  };

  /**
   * Get specific query parameter
   * @param {string} key - Query parameter key
   * @returns {string|null} Query parameter value or null
   */
  const getQueryParam = (key) => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get(key);
  };

  return {
    location,
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
    getPathSegments,
    isInRoute,
    getRouteParam,
    isInChat,
    getCurrentChatId,
    isInSection,
    getQueryParams,
    getQueryParam,
  };
};

export default useUrlManager;
